/**
 * O que faz: Carrega o ranking completo do card de anúncios (por anúncio, conjunto ou campanha)
 *            e permite pré-carregar uma aba antes do clique.
 * Onde usa: AdPerformanceCard (lista com rolagem e abas do Dashboard).
 * Conexões: React Query, contexto de sessão e RPC get_dashboard_ranking_origens_v1.
 * Tabelas: SELECT agregado de leads/agenda/aliases/lead_ad_attributions, só da clínica autorizada.
 * Data: 09/10/2026.
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContextoDashboard, DimensaoRankingAnuncios } from '@/features/dashboard/data/dashboardContracts';
import { buscarRankingOrigensDashboard } from '@/services/dashboardResumoService';
import { chaveContextoDashboard } from './useDashboardConsultas';

// Chave e tempos num só lugar: o pré-carregamento precisa usar exatamente a mesma chave da tela.
const opcoesRankingOrigens = (contexto: ContextoDashboard | undefined, dimensao: DimensaoRankingAnuncios) => ({
  // A chave começa com 'dashboard-v1' para ser invalidada junto com o resto do dashboard
  // (ex.: ao salvar um apelido, que já invalida esse prefixo).
  queryKey: ['dashboard-v1', ...chaveContextoDashboard(contexto), 'ranking-origens', contexto?.periodo, dimensao],
  queryFn: ({ signal }: { signal: AbortSignal }) => buscarRankingOrigensDashboard(contexto!, dimensao, signal),
  staleTime: 60_000,
  gcTime: 5 * 60_000,
});

// `habilitado` permite deixar a consulta parada quando ela não é necessária.
export function useRankingOrigensDashboard(contexto: ContextoDashboard | undefined, dimensao: DimensaoRankingAnuncios, habilitado = true) {
  return useQuery({
    ...opcoesRankingOrigens(contexto, dimensao),
    enabled: !!contexto && habilitado,
    // Mantém a lista anterior na tela enquanto a nova carrega (troca de aba ou de período), para a
    // transição ser suave em vez de piscar vazio. A tela sabe que é provisória por isPlaceholderData
    // e lê a aba real em data.dimensao, então nunca rotula anúncios como campanhas.
    placeholderData: (anterior) => anterior,
    retry: 1, refetchOnWindowFocus: false,
  });
}

// Pré-carrega uma aba ao passar o mouse/focar no botão: quando a pessoa clica, a lista já está pronta.
// prefetchQuery não repete a leitura se o dado ainda está fresco (staleTime de 1 minuto).
export function usePrefetchRankingOrigens(contexto: ContextoDashboard | undefined) {
  const queryClient = useQueryClient();
  return useCallback((dimensao: DimensaoRankingAnuncios) => {
    if (!contexto) return;
    void queryClient.prefetchQuery(opcoesRankingOrigens(contexto, dimensao));
  }, [contexto, queryClient]);
}
